import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BrainCircuit, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";
import { useCurrency } from "../../context/CurrencyContext";

const ROTATE_MS = 8000;

export default function AiSaysCard() {
  const { format } = useCurrency();
  const [raw, setRaw] = useState(null);
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get("/insights/ai").then((res) => setRaw(res.data.insights)).catch(() => setRaw([]));
  }, []);

  const messages = useMemo(() => {
    if (!raw) return [];
    const out = [];
    for (const ins of raw) {
      if (ins.type === "price_move") {
        out.push({
          id: "price_move",
          text: `${ins.name} has ${ins.pct > 0 ? "risen" : "fallen"} ${Math.abs(ins.pct)}% since you scanned it.`,
          to: `/card/${ins.card_id}`, actionLabel: "View card",
        });
      } else if (ins.type === "duplicates") {
        out.push({
          id: "duplicates",
          text: `You have ${format(ins.value_usd)} of duplicate cards that could be sold.`,
        });
      } else if (ins.type === "set_completion") {
        out.push({
          id: "set_completion",
          text: `You're missing ${ins.missing} card${ins.missing > 1 ? "s" : ""} to complete ${ins.name}.`,
        });
      } else if (ins.type === "chase") {
        out.push({
          id: "chase",
          text: `${ins.name} on your ChaseDeck is valued around ${format(ins.value_usd)}${ins.owner_count > 0 ? ` — ${ins.owner_count} collector${ins.owner_count > 1 ? "s" : ""} here own${ins.owner_count > 1 ? "" : "s"} one` : ""}.`,
          to: "/community", actionLabel: "Explore",
        });
      } else if (ins.type === "ai_review") {
        out.push({
          id: "ai_review",
          text: `${ins.count} scan${ins.count > 1 ? "s are" : " is"} waiting for your review — a quick confirm keeps valuations accurate.`,
        });
      } else if (ins.type === "welcome") {
        out.push({
          id: "welcome",
          text: ins.total_cards > 0
            ? `Your collection is worth ${format(ins.total_value_usd)} across ${ins.total_cards} card${ins.total_cards > 1 ? "s" : ""} — scan more to grow it.`
            : "Scan your first cards and I'll start finding insights for you.",
        });
      }
    }
    return out;
  }, [raw, format]);

  useEffect(() => {
    if (messages.length < 2) return;
    timerRef.current = setInterval(() => setIndex((i) => (i + 1) % messages.length), ROTATE_MS);
    return () => clearInterval(timerRef.current);
  }, [messages.length]);

  if (!messages.length) return null;

  const step = (dir) => {
    clearInterval(timerRef.current);
    setIndex((i) => (i + dir + messages.length) % messages.length);
    timerRef.current = setInterval(() => setIndex((i) => (i + 1) % messages.length), ROTATE_MS);
  };

  const msg = messages[index % messages.length];

  return (
    <div
      data-testid="ai-says-card"
      className="relative overflow-hidden border border-[#A78BFA]/25 bg-[#111113] rounded-2xl px-5 py-4 mb-6 flex items-center gap-4"
    >
      <div className="absolute inset-0 bg-gradient-to-r from-[#A78BFA]/10 to-transparent pointer-events-none" />
      <div className="relative w-9 h-9 rounded-full bg-[#7C3AED]/25 border border-[#A78BFA]/40 flex items-center justify-center shrink-0">
        <BrainCircuit className="w-5 h-5 text-[#A78BFA]" />
      </div>
      <div className="relative min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#A78BFA] font-semibold mb-0.5">CardArk AI says…</p>
        <p key={msg.id + index} data-testid="ai-says-text" className="text-sm text-zinc-200 animate-fade-up" style={{ animationDuration: "400ms" }}>
          {msg.text}
          {msg.to && (
            <Link to={msg.to} data-testid="ai-says-action" className="ml-2 text-[#A78BFA] hover:underline underline-offset-2 text-xs font-medium whitespace-nowrap">
              {msg.actionLabel} →
            </Link>
          )}
        </p>
      </div>
      {messages.length > 1 && (
        <div className="relative flex items-center gap-1 shrink-0">
          <button data-testid="ai-says-prev" onClick={() => step(-1)} className="w-7 h-7 rounded-full border border-white/10 flex items-center justify-center text-zinc-500 hover:text-white hover:border-white/30 transition-colors">
            <ChevronLeft className="w-3.5 h-3.5" />
          </button>
          <span className="text-[10px] text-zinc-600 font-mono-plex px-1">{(index % messages.length) + 1}/{messages.length}</span>
          <button data-testid="ai-says-next" onClick={() => step(1)} className="w-7 h-7 rounded-full border border-white/10 flex items-center justify-center text-zinc-500 hover:text-white hover:border-white/30 transition-colors">
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}
